import React, { useState, useEffect } from "react";
import { Modal, Tabs, Tab } from "react-bootstrap";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Link } from "react-router-dom";
import Header from "./../header";
import Navvbar from "./../Navvbar";
import "./calender.css";

// Card icons (same as Home.js)
import { GrDeliver } from "react-icons/gr";
import { TiDownloadOutline } from "react-icons/ti";
import { LuPackage2 } from "react-icons/lu";
import { BiTask } from "react-icons/bi";
import { MdOutlineLaunch } from "react-icons/md";
import { SlCalender } from "react-icons/sl";
import { MdOutlineContentCopy } from "react-icons/md";

const daysOfWeek = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

// Monday-first labels (used only by the small-screen 7 column layout)
const weekHeaders = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

// =====================================================
// HELPER FUNCTIONS
// =====================================================

const formatDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isSameDate = (date1, date2) => {
  if (!date1 || !date2) return false;
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return false;
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
};

const Calender = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [casesData, setCasesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userLabId, setUserLabId] = useState(null);
  const [showSaturday, setShowSaturday] = useState(false);

  // Small-screen "Actions" dropdown
  const [showActions, setShowActions] = useState(false);

  // Modal state
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEventDate, setSelectedEventDate] = useState(null);
  const [key, setKey] = useState("cases");

  // Hover popup state
  const [hoveredCase, setHoveredCase] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });

  // =====================================================
  // GET USER LAB ID
  // =====================================================
  useEffect(() => {
    const labId = localStorage.getItem("userLabId");
    const userDataStr =
      localStorage.getItem("userData") || sessionStorage.getItem("userData");

    let finalLabId = labId;
    if (!finalLabId && userDataStr) {
      try {
        const userData = JSON.parse(userDataStr);
        if (userData.labId) finalLabId = userData.labId;
      } catch (e) {}
    }

    if (finalLabId) {
      setUserLabId(finalLabId);
    } else {
      setError("User Lab ID not found. Please login again.");
      setLoading(false);
    }

    const saturdayPreference = localStorage.getItem("showSaturday");
    if (saturdayPreference !== null) {
      setShowSaturday(saturdayPreference === "true");
    }
  }, []);

  // =====================================================
  // FETCH CASES DATA
  // =====================================================
  useEffect(() => {
    if (userLabId) {
      fetchCasesData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLabId, currentMonth, currentYear]);

  const fetchCasesData = async () => {
    try {
      setLoading(true);
      setError(null);

      // include the greyed days of previous / next month shown in the
      // small-screen 7 column grid, so their counts are real too
      const firstOfMonth = new Date(currentYear, currentMonth, 1);
      const daysInThisMonth = new Date(
        currentYear,
        currentMonth + 1,
        0
      ).getDate();
      const lead = (firstOfMonth.getDay() + 6) % 7;
      const trail = (7 - ((lead + daysInThisMonth) % 7)) % 7;
      const startDate = new Date(currentYear, currentMonth, 1 - lead);
      const endDate = new Date(currentYear, currentMonth + 1, trail);

      const startDateStr = formatDate(startDate);
      const endDateStr = formatDate(endDate);

      const url = `https://hms.backend.hiragroupcloud.com/api/cases/lab-cases/${userLabId}?startDate=${startDateStr}&endDate=${endDateStr}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const resData = await response.json();

      let fetchedCases = [];
      if (resData.success && Array.isArray(resData.data)) {
        fetchedCases = resData.data;
      } else if (Array.isArray(resData)) {
        fetchedCases = resData;
      } else if (resData && resData.data && Array.isArray(resData.data)) {
        fetchedCases = resData.data;
      }

      setCasesData(fetchedCases);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setCasesData([]);
      setLoading(false);
    }
  };

  // =====================================================
  // COUNT FUNCTIONS
  // =====================================================
  const getCasesCountForDate = (date) => {
    if (loading || !Array.isArray(casesData)) return 0;
    return casesData.filter((caseItem) => {
      if (!caseItem) return false;
      const caseDate = caseItem.date || caseItem.start_date;
      return isSameDate(caseDate, date);
    }).length;
  };

  const getArriveCountForDate = (date) => {
    if (loading || !Array.isArray(casesData)) return 0;
    return casesData.filter((caseItem) => {
      if (!caseItem || !caseItem.arrive_date) return false;
      return isSameDate(caseItem.arrive_date, date);
    }).length;
  };

  const getShipCountForDate = (date) => {
    if (loading || !Array.isArray(casesData)) return 0;
    return casesData.filter((caseItem) => {
      if (!caseItem || !caseItem.delivery_date) return false;
      return isSameDate(caseItem.delivery_date, date);
    }).length;
  };

  const getDeliveryCountForDate = (date) => {
    if (loading || !Array.isArray(casesData)) return 0;
    return casesData.filter((caseItem) => {
      if (!caseItem || !caseItem.delivery_date) return false;
      return isSameDate(caseItem.delivery_date, date);
    }).length;
  };

  // =====================================================
  // GENERATE MONTHLY DATA
  // =====================================================
  const generateDays = (startDate, daysInMonth) => {
    const monthData = [];
    const today = new Date();

    for (let i = 0; i < daysInMonth; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);

      const isToday = isSameDate(date, today);

      monthData.push({
        date,
        isToday,
        weekDay: daysOfWeek[date.getDay()],
        events: [
          {
            label: "New Cases",
            tabKey: "cases",
            count: getCasesCountForDate(date),
            icon: <LuPackage2 size={20} />,
          },
          {
            label: "Arrive",
            tabKey: "cases",
            count: getArriveCountForDate(date),
            icon: <TiDownloadOutline size={20} />,
          },
          {
            label: "Ship",
            tabKey: "shipping",
            count: getShipCountForDate(date),
            icon: <MdOutlineLaunch size={20} />,
          },
          {
            label: "Tasks",
            tabKey: "tasks",
            count: 0,
            icon: <BiTask size={20} />,
          },
          {
            label: "Deliveries",
            tabKey: "shipping",
            count: getDeliveryCountForDate(date),
            icon: <GrDeliver size={20} />,
          },
        ],
      });
    }
    return monthData;
  };

  const daysInCurrentMonth = new Date(
    currentYear,
    currentMonth + 1,
    0
  ).getDate();
  const monthData = generateDays(
    new Date(currentYear, currentMonth, 1),
    daysInCurrentMonth
  );

  // Previous / next month days so the small-screen grid lines up Monday–Sunday
  const leadingPad = (new Date(currentYear, currentMonth, 1).getDay() + 6) % 7;
  const trailingPad = (7 - ((leadingPad + daysInCurrentMonth) % 7)) % 7;
  const prevDays = generateDays(
    new Date(currentYear, currentMonth, 1 - leadingPad),
    leadingPad
  );
  const nextDays = generateDays(
    new Date(currentYear, currentMonth + 1, 1),
    trailingPad
  );

  // =====================================================
  // PREVIOUS / NEXT MONTH
  // =====================================================
  const handlePreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  // =====================================================
  // EVENT CLICK
  // =====================================================
  const handleEventClick = (event, date) => {
    setKey(event.tabKey || "cases");
    setSelectedEventDate(date);
    setShowEventModal(true);
  };

  const handleCloseEventModal = () => {
    setShowEventModal(false);
  };

  // =====================================================
  // HOVER
  // =====================================================
  const handleMouseEnter = (event, caseItem) => {
    setHoveredCase(caseItem);
    setPopupPosition({
      x: event.clientX,
      y: event.clientY,
    });
  };

  const handleMouseLeave = () => {
    setHoveredCase(null);
  };

  const toggleSaturday = () => {
    const newValue = !showSaturday;
    setShowSaturday(newValue);
    localStorage.setItem("showSaturday", newValue.toString());
  };

  // =====================================================
  // LOADING / ERROR
  // =====================================================
  if (loading) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <Header />
        <Navvbar />
        <h2>Loading calendar...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "20px", textAlign: "center", color: "red" }}>
        <Header />
        <Navvbar />
        <h2>Error loading data</h2>
        <p>{error}</p>
        <button onClick={fetchCasesData}>Retry</button>
      </div>
    );
  }

  // =====================================================
  // MAIN UI
  // =====================================================
  return (
    <div className="cal-page">
      <div className="cal-top-bars">
        <Header />
        <Navvbar />
      </div>

      {/* MONTH NAVIGATION HEADER */}
      <div className="cal-events-header">
        <h5 className="cal-title">
          <SlCalender />
          <strong>
            {`${new Date(currentYear, currentMonth).toLocaleString("default", {
              month: "long",
            })} ${currentYear}`}
          </strong>
        </h5>

        <div className="cal-header-right">
          {/* Large screen: Saturday toggle */}
          <div
            className="cal-saturday-toggle"
            onClick={toggleSaturday}
            title={showSaturday ? "Hide Saturday" : "Show Saturday"}
          >
            <span>Saturday</span>
            <span className="cal-sat-icon">{showSaturday ? "🟢" : "⚪"}</span>
          </div>

          {/* Small screen: Actions button (holds the Saturday toggle) */}
          <div className="cal-actions-wrap">
            <button
              className="cal-actions-btn"
              onClick={() => setShowActions(!showActions)}
            >
              Actions ▾
            </button>
            {showActions && (
              <div className="cal-actions-dropdown">
                <div className="cal-actions-item" onClick={toggleSaturday}>
                  <span>Saturday</span>
                  <span>{showSaturday ? "🟢" : "⚪"}</span>
                </div>
              </div>
            )}
          </div>

          <div className="cal-nav-buttons">
            <button className="cal-nav-btn" onClick={handlePreviousMonth}>
              &larr; Prev
            </button>
            <button className="cal-nav-btn" onClick={handleNextMonth}>
              Next &rarr;
            </button>
          </div>
        </div>
      </div>

      {/* FULL MONTH GRID */}
      <div className="cal-grid-container">
        {/* weekday labels (visible on small screens only) */}
        {weekHeaders.map((w) => (
          <div key={`wk-${w}`} className="cal-weekday">
            {w}
          </div>
        ))}

        {/* previous / next month days (small screens only) + this month */}
        {[
          ...prevDays.map((d) => ({ ...d, outside: true })),
          ...monthData,
          ...nextDays.map((d) => ({ ...d, outside: true })),
        ].map((item, i) => (
          <div
            key={i}
            className={`cal-day-card ${item.isToday ? "is-today" : ""} ${
              item.outside ? "is-outside" : ""
            }`}
          >
            {/* CARD HEADER */}
            <div className="cal-card-header">
              <span className="cal-date-full">
                {item.date.toString().slice(0, 15)}
              </span>
              <span className="cal-date-short">
                {String(item.date.getDate()).padStart(2, "0")}
              </span>
              <MdOutlineContentCopy
                className="cal-copy-icon"
                onClick={() =>
                  handleEventClick({ tabKey: "cases" }, item.date)
                }
                title="View Events"
              />
            </div>

            {/* EVENTS */}
            <div className="cal-card-body">
              {item.events.map((event, idx) => (
                <button
                  key={idx}
                  className={`cal-event-btn ${
                    event.count === 0 ? "is-zero" : ""
                  }`}
                  onClick={() => handleEventClick(event, item.date)}
                >
                  <span className="cal-event-icon">{event.icon}</span>
                  <span className="cal-event-text">
                    {event.label}: {event.count}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Bottom Previous / Next (small screens only) */}
      <div className="cal-bottom-nav">
        <button className="cal-nav-btn" onClick={handlePreviousMonth}>
          &larr; Previous
        </button>
        <button className="cal-nav-btn" onClick={handleNextMonth}>
          Next &rarr;
        </button>
      </div>

      {/* =================================================
          EVENTS MODAL
      ================================================= */}
      <Modal
        show={showEventModal}
        onHide={handleCloseEventModal}
        centered
        dialogClassName="custom-modal-dialog"
      >
        <Modal.Header className="cal-modal-header">
          <Modal.Title>Events</Modal.Title>

          <button className="cal-modal-close" onClick={handleCloseEventModal}>
            &times;
          </button>
        </Modal.Header>

        <Modal.Body>
          <Tabs
            id="controlled-tab-example"
            activeKey={key}
            onSelect={(k) => setKey(k)}
            className="mb-3"
          >
            {/* CASES TAB */}
            <Tab eventKey="cases" title="Cases">
              <div className="p-3">
                <h5 className="font-bold">New Cases</h5>
                <ul>
                  {Array.isArray(casesData) &&
                    casesData
                      .filter((caseItem) => {
                        if (!caseItem) return false;
                        const caseDate =
                          caseItem.date || caseItem.start_date;
                        return isSameDate(caseDate, selectedEventDate);
                      })
                      .map((caseItem, index) => (
                        <li
                          key={`case-${index}`}
                          onMouseEnter={(e) =>
                            handleMouseEnter(e, caseItem)
                          }
                          onMouseLeave={handleMouseLeave}
                          style={{ cursor: "pointer" }}
                        >
                          <Link
                            to={`/case/${caseItem.case_id || caseItem._id}`}
                            style={{ textDecoration: "none", color: "inherit" }}
                          >
                            Case{" "}
                            <span style={{ color: "#004aad" }}>
                              #{caseItem.case_id || caseItem._id}
                            </span>
                          </Link>
                        </li>
                      ))}
                </ul>

                <h5 className="font-bold mt-4">Cases Arriving</h5>
                <ul>
                  {Array.isArray(casesData) &&
                    casesData
                      .filter((caseItem) => {
                        if (!caseItem || !caseItem.arrive_date) return false;
                        return isSameDate(
                          caseItem.arrive_date,
                          selectedEventDate
                        );
                      })
                      .map((caseItem, index) => (
                        <li
                          key={`arrive-${index}`}
                          onMouseEnter={(e) =>
                            handleMouseEnter(e, caseItem)
                          }
                          onMouseLeave={handleMouseLeave}
                          style={{ cursor: "pointer" }}
                        >
                          <Link
                            to={`/case/${caseItem.case_id || caseItem._id}`}
                            style={{ textDecoration: "none", color: "inherit" }}
                          >
                            Case{" "}
                            <span style={{ color: "#004aad" }}>
                              #{caseItem.case_id || caseItem._id}
                            </span>
                          </Link>{" "}
                          to arrive
                        </li>
                      ))}
                </ul>

                <h5 className="font-bold mt-4">Cases Shipping</h5>
                <ul>
                  {Array.isArray(casesData) &&
                    casesData
                      .filter((caseItem) => {
                        if (!caseItem || !caseItem.delivery_date) return false;
                        return isSameDate(
                          caseItem.delivery_date,
                          selectedEventDate
                        );
                      })
                      .map((caseItem, index) => (
                        <li
                          key={`ship-${index}`}
                          onMouseEnter={(e) =>
                            handleMouseEnter(e, caseItem)
                          }
                          onMouseLeave={handleMouseLeave}
                          style={{ cursor: "pointer" }}
                        >
                          <Link
                            to={`/case/${caseItem.case_id || caseItem._id}`}
                            style={{ textDecoration: "none", color: "inherit" }}
                          >
                            Case{" "}
                            <span style={{ color: "#004aad" }}>
                              #{caseItem.case_id || caseItem._id}
                            </span>
                          </Link>{" "}
                          to ship
                        </li>
                      ))}
                </ul>
              </div>
            </Tab>

            {/* ACTIVITY TAB */}
            <Tab eventKey="activity" title="Activity">
              <p>Activity content will go here.</p>
            </Tab>

            {/* TASKS TAB */}
            <Tab eventKey="tasks" title="Tasks">
              <div className="p-3">{/* Tasks content */}</div>
            </Tab>

            {/* SHIPPING TAB */}
            <Tab eventKey="shipping" title="Shipping">
              <h5 className="font-bold mt-4">Cases Shipping</h5>
              <ul>
                {Array.isArray(casesData) &&
                  casesData
                    .filter((caseItem) => {
                      if (!caseItem || !caseItem.delivery_date) return false;
                      return isSameDate(
                        caseItem.delivery_date,
                        selectedEventDate
                      );
                    })
                    .map((caseItem, index) => (
                      <li
                        key={`ship-${index}`}
                        onMouseEnter={(e) =>
                          handleMouseEnter(e, caseItem)
                        }
                        onMouseLeave={handleMouseLeave}
                        style={{ cursor: "pointer" }}
                      >
                        <Link
                          to={`/case/${caseItem.case_id || caseItem._id}`}
                          style={{ textDecoration: "none", color: "inherit" }}
                        >
                          Case{" "}
                          <span style={{ color: "#004aad" }}>
                            #{caseItem.case_id || caseItem._id}
                          </span>
                        </Link>{" "}
                        to ship
                      </li>
                    ))}
              </ul>
            </Tab>
          </Tabs>
        </Modal.Body>
      </Modal>

      {/* =================================================
          HOVER POPUP
      ================================================= */}
      {hoveredCase && (
        <div
          style={{
            position: "fixed",
            top: popupPosition.y + 10,
            left: popupPosition.x + 10,
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: "4px",
            padding: "15px",
            boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
            zIndex: 9999,
            width: "200px",
            fontSize: "14px",
            lineHeight: "1.5",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <span style={{ marginRight: "5px" }}>
              Case{" "}
              <span style={{ color: "#004aad", fontSize: "12px" }}>
                #{hoveredCase.case_id || hoveredCase._id}
              </span>
            </span>

            <span style={{ marginRight: "5px" }}>for</span>

            <img
              src="/logo1.jpg"
              alt="Logolab"
              style={{ width: "40px", height: "auto" }}
            />
          </div>

          <div>
            <span style={{ color: "#004aad", fontSize: "12px" }}>
              {hoveredCase.last_name || ""}
            </span>
            <span style={{ marginLeft: "5px" }}>for</span>
          </div>

          <div style={{ marginBottom: "8px" }}>
            <span style={{ color: "#004aad", fontSize: "12px" }}>
              {hoveredCase.account_name ||
                hoveredCase.account_holder ||
                "Unknown Account"}
            </span>
          </div>

          {/* PROCEDURES */}
          {hoveredCase.procedures && hoveredCase.procedures.length > 0 && (
            <div style={{ marginBottom: "8px" }}>
              {hoveredCase.procedures.map((procedure, index) => (
                <div
                  key={index}
                  style={{ marginBottom: "8px", padding: "5px" }}
                >
                  <div style={{ color: "gray", fontSize: "12px" }}>
                    {procedure.procedure}
                  </div>

                  {procedure.teeth && (
                    <div style={{ color: "gray", fontSize: "11px" }}>
                      <strong>Teeth:</strong> {procedure.teeth}
                    </div>
                  )}

                  {procedure.shade && (
                    <div style={{ color: "gray", fontSize: "11px" }}>
                      <strong>Shade:</strong> {procedure.shade}
                    </div>
                  )}

                  {procedure.alloyMaterial && (
                    <div style={{ color: "gray", fontSize: "11px" }}>
                      <strong>Material:</strong> {procedure.alloyMaterial}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* FALLBACK */}
          {(!hoveredCase.procedures ||
            hoveredCase.procedures.length === 0) &&
            hoveredCase.workRequested && (
              <div style={{ marginBottom: "8px" }}>
                <span
                  style={{
                    color: "#004aad",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}
                >
                  {hoveredCase.workRequested}
                </span>
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default Calender;